import _ from 'lodash';
import { makeObservable, override } from 'mobx';
import {
    CampaignApiFindAllCampaignsRequest,
    CampaignDto,
    CampaignDtoBrief,
    CreateCampaignRequest,
    UpdateCampaignRequest,
} from 'src/generated-api-client';
import {
    bpmsResourcesManagerApi,
    campaignsApi,
} from 'src/services/apiServices';
import { ListFilter } from 'src/utils/FormHelper';
import { BasicStore } from 'src/utils/mobx/BasicStore/BasicStore';
import { BasicStoreApi } from 'src/utils/mobx/BasicStore/BasicStore.types';
import { FilterCriteria } from 'src/utils/mobx/FilterCriteria';
import { Pager } from 'src/utils/mobx/Pager';
import { Sorter } from 'src/utils/mobx/Sorter';
import { RequestHelper } from 'src/utils/RequestHelper';

export type CampaignsFilter = ListFilter<CampaignApiFindAllCampaignsRequest>;

export class CampaignsStoreClass extends BasicStore<
    CampaignDtoBrief,
    CampaignsFilter,
    UpdateCampaignRequest,
    CreateCampaignRequest,
    CampaignDto
> {
    constructor() {
        super();
        makeObservable(this);
    }

    itemDetailsFormKey = 'embedded:deployment:forms/bo/bo-19-campaign.json';

    itemEditFormKey = 'embedded:deployment:forms/bo/bo-21-edit-campaign.json';

    itemCreateFormKey =
        'embedded:deployment:forms/bo/bo-20-create-campaign.json';

    pager = new Pager();
    sorter = new Sorter();
    filterCriteria: FilterCriteria<CampaignsFilter> = new FilterCriteria();

    api: BasicStoreApi<
        CampaignDtoBrief,
        UpdateCampaignRequest,
        CreateCampaignRequest,
        CampaignDto
    > = {
        loadList: async (...args) => {
            const options = RequestHelper.getOptionsFromArgs(args);
            const resp = await RequestHelper.unwrapFromAxiosPromise(
                campaignsApi.findAllCampaigns(
                    {
                        ...this.pager,
                        ...this.sorter,
                        ...this.filterCriteria.filter,
                    },
                    options,
                ),
            );

            this.pager.setTotal(resp.totalElements || 0);

            return resp.content || [];
        },

        loadItemForm: (formKey: string, ...args) => {
            const options = RequestHelper.getOptionsFromArgs(args);

            return RequestHelper.unwrapFromAxiosPromise(
                bpmsResourcesManagerApi.getForm({ formKey }, options),
            );
        },
        loadItem: (id: number, ...args) => {
            const options = RequestHelper.getOptionsFromArgs(args);

            return RequestHelper.unwrapFromAxiosPromise(
                campaignsApi.getCampaign({ id }, options),
            );
        },
        updateItem: (id, data, ...args) => {
            const options = RequestHelper.getOptionsFromArgs(args);

            return RequestHelper.unwrapFromAxiosPromise(
                campaignsApi.updateCampaign(
                    { id: Number(id), updateCampaignRequest: data },
                    options,
                ),
            );
        },
        createItem: (data, ...args) => {
            const options = RequestHelper.getOptionsFromArgs(args);

            return RequestHelper.unwrapFromAxiosPromise(
                campaignsApi.createCampaign(
                    { createCampaignRequest: data },
                    options,
                ),
            );
        },
    };

    @override get hasFilter() {
        const { filterSearch, filterMaxTerm, filterMaxAmount } =
            this.filterCriteria.filter;

        return (
            !_.isNil(filterMaxAmount) ||
            !_.isNil(filterMaxTerm) ||
            !_.isNil(filterSearch)
        );
    }
}

export const CampaignsStore = new CampaignsStoreClass();
